import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import AppLayout from './layouts/AppLayout';
import { Spinner } from '@school-erp/ui';

// ─── Lazy-loaded Pages ───
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const AdvancedDashboardPage = lazy(() => import('./pages/dashboard/AdvancedDashboardPage'));
const StudentsPage = lazy(() => import('./pages/students/StudentsPage'));
const StudentDetailPage = lazy(() => import('./pages/students/StudentDetailPage'));
const StudentCreatePage = lazy(() => import('./pages/students/StudentCreatePage'));
const StudentEditPage = lazy(() => import('./pages/students/StudentEditPage'));
const StudentPromotionPage = lazy(() => import('./pages/students/StudentPromotionPage'));
const EmployeesPage = lazy(() => import('./pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('./pages/employees/EmployeeDetailPage'));
const EmployeeCreatePage = lazy(() => import('./pages/employees/EmployeeCreatePage'));
const EmployeeEditPage = lazy(() => import('./pages/employees/EmployeeEditPage'));
const GuardiansPage = lazy(() => import('./pages/guardians/GuardiansPage'));
const GuardianDetailPage = lazy(() => import('./pages/guardians/GuardianDetailPage'));
const GuardianEditPage = lazy(() => import('./pages/guardians/GuardianEditPage'));
const AcademicsPage = lazy(() => import('./pages/academics/AcademicsPage'));
const AttendancePage = lazy(() => import('./pages/attendance/AttendancePage'));
const AssignmentsPage = lazy(() => import('./pages/assignments/AssignmentsPage'));
const ExamsPage = lazy(() => import('./pages/exams/ExamsPage'));
const GradingPage = lazy(() => import('./pages/exams/GradingPage'));
const FeesPage = lazy(() => import('./pages/fees/FeesPage'));
const FeeEnhancementsPage = lazy(() => import('./pages/fees/FeeEnhancementsPage'));
const HrPage = lazy(() => import('./pages/hr/HrPage'));
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'));
const CommunicationPage = lazy(() => import('./pages/communication/CommunicationPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'));
const AuditPage = lazy(() => import('./pages/audit/AuditPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const TeachersPage = lazy(() => import('./pages/teachers/TeachersPage'));
const TimetablePage = lazy(() => import('./pages/timetable/TimetablePage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const EnhancedReportsPage = lazy(() => import('./pages/reports/EnhancedReportsPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const MyClassesPage = lazy(() => import('./pages/my-classes/MyClassesPage'));
const MyWardsPage = lazy(() => import('./pages/my-wards/MyWardsPage'));
const MaterialsPage = lazy(() => import('./pages/materials/MaterialsPage'));
const LibraryPage = lazy(() => import('./pages/library/LibraryPage'));
const TransportPage = lazy(() => import('./pages/transport/TransportPage'));
const FrontOfficePage = lazy(() => import('./pages/front-office/FrontOfficePage'));
const HostelPage = lazy(() => import('./pages/hostel/HostelPage'));
const InfirmaryPage = lazy(() => import('./pages/infirmary/InfirmaryPage'));
const HealthWelfarePage = lazy(() => import('./pages/infirmary/HealthWelfarePage'));
const EventsPage = lazy(() => import('./pages/events/EventsPage'));
const AdmissionsPage = lazy(() => import('./pages/admissions/AdmissionsPage'));
const CertificatesPage = lazy(() => import('./pages/certificates/CertificatesPage'));
const DisciplinePage = lazy(() => import('./pages/discipline/DisciplinePage'));
const SubstitutionsPage = lazy(() => import('./pages/substitutions/SubstitutionsPage'));
const GrievancesPage = lazy(() => import('./pages/grievances/GrievancesPage'));
const AccountingPage = lazy(() => import('./pages/accounting/AccountingPage'));
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'));
const HrEnhancementsPage = lazy(() => import('./pages/hr/HrEnhancementsPage'));
const AssetRegisterPage = lazy(() => import('./pages/inventory/AssetRegisterPage'));
const LessonPlansPage = lazy(() => import('./pages/lesson-plans/LessonPlansPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={user ? <Navigate to="/" replace /> : <ResetPasswordPage />} />

        {/* Protected routes under layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="dashboard/advanced" element={<AdvancedDashboardPage />} />

          {/* Students */}
          <Route
            path="students"
            element={
              <ProtectedRoute permissions={['students.read']}>
                <StudentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="students/new"
            element={
              <ProtectedRoute permissions={['students.write']}>
                <StudentCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="students/promote"
            element={
              <ProtectedRoute permissions={['students.lifecycle.manage']}>
                <StudentPromotionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="students/:studentId/edit"
            element={
              <ProtectedRoute permissions={['students.write']}>
                <StudentEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="students/:studentId"
            element={
              <ProtectedRoute permissions={['students.read']}>
                <StudentDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Employees */}
          <Route
            path="employees"
            element={
              <ProtectedRoute permissions={['employees.read']}>
                <EmployeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="employees/new"
            element={
              <ProtectedRoute permissions={['employees.write']}>
                <EmployeeCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="employees/:employeeId/edit"
            element={
              <ProtectedRoute permissions={['employees.write']}>
                <EmployeeEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="employees/:employeeId"
            element={
              <ProtectedRoute permissions={['employees.read']}>
                <EmployeeDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Guardians */}
          <Route
            path="guardians"
            element={
              <ProtectedRoute permissions={['guardians.read']}>
                <GuardiansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="guardians/:guardianId/edit"
            element={
              <ProtectedRoute permissions={['guardians.write']}>
                <GuardianEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="guardians/:guardianId"
            element={
              <ProtectedRoute permissions={['guardians.read']}>
                <GuardianDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="academics"
            element={
              <ProtectedRoute permissions={['academics.read']}>
                <AcademicsPage />
              </ProtectedRoute>
            }
          />

          {/* Teachers */}
          <Route
            path="teachers"
            element={
              <ProtectedRoute permissions={['employees.read']}>
                <TeachersPage />
              </ProtectedRoute>
            }
          />

          {/* Timetable */}
          <Route
            path="timetable"
            element={
              <ProtectedRoute permissions={['academics.read']}>
                <TimetablePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="attendance"
            element={
              <ProtectedRoute permissions={['attendance.read']}>
                <AttendancePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="assignments"
            element={
              <ProtectedRoute permissions={['assignments.read']}>
                <AssignmentsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="exams"
            element={
              <ProtectedRoute permissions={['exams.read']}>
                <ExamsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="grading"
            element={
              <ProtectedRoute permissions={['exams.read']}>
                <GradingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="fees"
            element={
              <ProtectedRoute permissions={['fees.read']}>
                <FeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="fees/enhancements"
            element={
              <ProtectedRoute permissions={['fees.read']}>
                <FeeEnhancementsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="hr"
            element={
              <ProtectedRoute permissions={['hr.read']}>
                <HrPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/enhancements"
            element={
              <ProtectedRoute permissions={['hr.read']}>
                <HrEnhancementsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="inventory"
            element={
              <ProtectedRoute permissions={['inventory.read']}>
                <InventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="inventory/assets"
            element={
              <ProtectedRoute permissions={['inventory.read']}>
                <AssetRegisterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="communication"
            element={
              <ProtectedRoute permissions={['communication.read']}>
                <CommunicationPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="users"
            element={
              <ProtectedRoute permissions={['users.read']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/:userId"
            element={
              <ProtectedRoute permissions={['users.read']}>
                <UserDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Reports */}
          <Route
            path="reports"
            element={
              <ProtectedRoute permissions={['reports.read']}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="reports/enhanced"
            element={
              <ProtectedRoute permissions={['reports.read']}>
                <EnhancedReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="audit"
            element={
              <ProtectedRoute permissions={['audit.read']}>
                <AuditPage />
              </ProtectedRoute>
            }
          />

          <Route path="settings" element={<SettingsPage />} />
          <Route path="profile" element={<ProfilePage />} />

          {/* My Classes (teacher) */}
          <Route
            path="my-classes"
            element={
              <ProtectedRoute roles={['teacher']}>
                <MyClassesPage />
              </ProtectedRoute>
            }
          />

          {/* My Wards (guardian) */}
          <Route
            path="my-wards"
            element={
              <ProtectedRoute roles={['guardian']}>
                <MyWardsPage />
              </ProtectedRoute>
            }
          />

          {/* Materials */}
          <Route
            path="materials"
            element={
              <ProtectedRoute permissions={['assignments.read']}>
                <MaterialsPage />
              </ProtectedRoute>
            }
          />

          {/* Library */}
          <Route
            path="library"
            element={
              <ProtectedRoute permissions={['library.read']}>
                <LibraryPage />
              </ProtectedRoute>
            }
          />

          {/* Transport */}
          <Route
            path="transport"
            element={
              <ProtectedRoute permissions={['transport.read']}>
                <TransportPage />
              </ProtectedRoute>
            }
          />

          {/* Front Office */}
          <Route
            path="front-office"
            element={
              <ProtectedRoute permissions={['front_office.read']}>
                <FrontOfficePage />
              </ProtectedRoute>
            }
          />

          {/* Hostel & Mess */}
          <Route
            path="hostel"
            element={
              <ProtectedRoute permissions={['hostel.read']}>
                <HostelPage />
              </ProtectedRoute>
            }
          />

          {/* Infirmary */}
          <Route
            path="infirmary"
            element={
              <ProtectedRoute permissions={['infirmary.read']}>
                <InfirmaryPage />
              </ProtectedRoute>
            }
          />

          {/* Health & Welfare */}
          <Route
            path="health-welfare"
            element={
              <ProtectedRoute permissions={['infirmary.read']}>
                <HealthWelfarePage />
              </ProtectedRoute>
            }
          />

          {/* Events & Calendar */}
          <Route
            path="events"
            element={
              <ProtectedRoute permissions={['events.read']}>
                <EventsPage />
              </ProtectedRoute>
            }
          />

          {/* Admissions */}
          <Route
            path="admissions"
            element={
              <ProtectedRoute permissions={['admissions.read']}>
                <AdmissionsPage />
              </ProtectedRoute>
            }
          />

          {/* Certificates */}
          <Route
            path="certificates"
            element={
              <ProtectedRoute permissions={['certificates.read']}>
                <CertificatesPage />
              </ProtectedRoute>
            }
          />

          {/* Discipline & Incidents */}
          <Route
            path="discipline"
            element={
              <ProtectedRoute permissions={['discipline.read']}>
                <DisciplinePage />
              </ProtectedRoute>
            }
          />

          {/* Substitutions */}
          <Route
            path="substitutions"
            element={
              <ProtectedRoute permissions={['academics.read']}>
                <SubstitutionsPage />
              </ProtectedRoute>
            }
          />

          {/* Grievances */}
          <Route
            path="grievances"
            element={
              <ProtectedRoute permissions={['grievances.read']}>
                <GrievancesPage />
              </ProtectedRoute>
            }
          />

          {/* Accounting */}
          <Route
            path="accounting"
            element={
              <ProtectedRoute permissions={['accounting.read']}>
                <AccountingPage />
              </ProtectedRoute>
            }
          />

          {/* Alerts / Early Warning */}
          <Route
            path="alerts"
            element={
              <ProtectedRoute permissions={['alerts.read']}>
                <AlertsPage />
              </ProtectedRoute>
            }
          />

          {/* Lesson Plans & Quizzes */}
          <Route
            path="lesson-plans"
            element={
              <ProtectedRoute permissions={['lesson_plans.read']}>
                <LessonPlansPage />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppRoutes />
    </ErrorBoundary>
  );
}
